import { Fragment } from "react";
import type { BookedFlightItineraryWithDepartureTime } from "@b2bportal/air-booking-api";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@b2bportal/core-ui";
import { useI18nContext } from "@hopper-b2b/i18n";
import { Icon } from "@tenant/ui";
import clsx from "clsx";
import styles from "./FlightExtrasSection.module.scss";
import {
  useFlightExtrasSection,
  type AirportMapForDisplay,
  type FlightSeats,
  type LayoverSlice,
} from "./useFlightExtrasSection";
import { parseSegmentLocalDate } from "@/utilities/flights/segmentDateTime";

interface FlightExtrasSectionProps {
  itinerary: BookedFlightItineraryWithDepartureTime;
  airportMap?: AirportMapForDisplay | null;
  airlineMap?: Record<string, { displayName: string }>;
  className?: string;
}

export const FlightExtrasSection = ({
  itinerary,
  airportMap,
  airlineMap,
  className,
}: FlightExtrasSectionProps) => {
  const { t, formatDateTime, DateTimeFormatStyle } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, "FlightExtrasSection");

  const { outboundSeats, returnSeats, hasSeats, layoverSlices, hasLayovers } =
    useFlightExtrasSection(itinerary, airportMap, airlineMap);

  if (!hasSeats && !hasLayovers) {
    return null;
  }

  const renderSeatCard = (flightSeats: FlightSeats, idx: number) => (
    <div key={`${flightSeats.route}-${idx}`} className={cn("seat-card")}>
      <p className={cn("seat-route")}>
        {t("originToDestination", {
          origin: flightSeats.originDisplay,
          destination: flightSeats.destinationDisplay,
        })}
      </p>
      <ul className={cn("seat-assignments")}>
        {flightSeats.seats.map((assignment) => (
          <li
            key={`${assignment.passengerId}-${assignment.seatNumber}`}
            className={cn("seat-row")}
            aria-label={t("flightTrips.passengerSeatAriaLabel", {
              passengerName: assignment.passengerName,
              seatNumber: assignment.seatNumber,
            })}
          >
            <p className={cn("passenger-name")}>{assignment.passengerName}</p>
            <div className={cn("seat-number")}>
              {/* TODO: Add seat icon when available */}
              <p className={cn("seat-value")}>{assignment.seatNumber}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );

  const renderLayoverBadge = (layoverDurationMinutes: number) => {
    const hours = Math.floor(layoverDurationMinutes / 60);
    const minutes = layoverDurationMinutes % 60;

    let durationText = "";
    if (hours > 0 && minutes > 0) {
      durationText = t("flightTrips.layoverDuration", {
        hours: hours.toString(),
        minutes: minutes.toString(),
      });
    } else if (hours > 0) {
      durationText = t("flightTrips.layoverDurationHoursOnly", {
        hours: hours.toString(),
      });
    } else {
      durationText = t("flightTrips.layoverDurationMinutesOnly", {
        minutes: minutes.toString(),
      });
    }

    return (
      <div className={cn("layover-badge-between")}>
        <div className={cn("layover-badge")}>
          <Icon
            iconName={IconNameEnum.clock}
            className={cn("layover-badge-icon")}
          />
          <span className={cn("layover-duration")}>{durationText}</span>
        </div>
      </div>
    );
  };

  const renderLayoverSlice = (slice: LayoverSlice, idx: number) => {
    // Validate date before formatting. parseSegmentLocalDate keeps the
    // airport-local wall clock so the date is not shifted into the browser TZ.
    const sliceDate = parseSegmentLocalDate(slice.date);
    const formattedDate =
      slice.date && !isNaN(sliceDate.getTime())
        ? formatDateTime(
            sliceDate,
            DateTimeFormatStyle.ShortMonthDayShortWeekday
          )
        : "";

    const directionLabel =
      slice.direction === "outbound"
        ? t("flightTrips.outboundTo")
        : t("flightTrips.returnTo");

    return (
      <div key={`${slice.direction}-${idx}`} className={cn("layover-slice")}>
        <p className={cn("layover-slice-header")}>
          {t("flightTrips.layoverSliceHeader", {
            directionLabel,
            destination: slice.destinationDisplay,
            date: formattedDate,
          })}
        </p>
        <div className={cn("layover-segments")}>
          {slice.segments.map((segment, segIdx) => {
            // Validate segment dates. parseSegmentLocalDate keeps the
            // airport-local wall clock so times are not shifted into the
            // browser's timezone.
            const departureDate = parseSegmentLocalDate(segment.departureTime);
            const arrivalDate = parseSegmentLocalDate(segment.arrivalTime);
            const formattedDeparture =
              segment.departureTime && !isNaN(departureDate.getTime())
                ? formatDateTime(departureDate, DateTimeFormatStyle.ShortTime)
                : "--";
            const formattedArrival =
              segment.arrivalTime && !isNaN(arrivalDate.getTime())
                ? formatDateTime(arrivalDate, DateTimeFormatStyle.ShortTime)
                : "--";

            return (
              <Fragment key={`segment-${segIdx}`}>
                <div className={cn("layover-segment-card")}>
                  <div className={cn("layover-segment-content")}>
                    <div className={cn("layover-flight-graphic")}>
                      <Icon
                        iconName={IconNameEnum.airplaneDeparture}
                        className={cn("layover-icon")}
                      />
                      <div className={cn("layover-line")} />
                      <Icon
                        iconName={IconNameEnum.airplaneArrive}
                        className={cn("layover-icon")}
                      />
                    </div>
                    <div className={cn("layover-flight-info")}>
                      <p className={cn("layover-flight-time")}>
                        <span className={cn("time-bold")}>
                          {formattedDeparture}
                        </span>{" "}
                        - {segment.originDisplay}
                      </p>
                      <p className={cn("layover-flight-time")}>
                        <span className={cn("time-bold")}>
                          {formattedArrival}
                        </span>{" "}
                        - {segment.destinationDisplay}
                      </p>
                      <p className={cn("layover-airline-info")}>
                        {(() => {
                          const parts = [segment.airlineName];
                          const flightInfo = [
                            segment.airlineCode,
                            segment.flightNumber?.toString(),
                          ]
                            .filter(Boolean)
                            .join(" ");
                          if (flightInfo) parts.push(flightInfo);
                          if (segment.planeInfo) parts.push(segment.planeInfo);
                          return parts.join(" / ");
                        })()}
                      </p>
                    </div>
                  </div>
                </div>
                {segIdx < slice.segments.length - 1 &&
                  segment.layoverDurationMinutes !== undefined &&
                  renderLayoverBadge(segment.layoverDurationMinutes)}
              </Fragment>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className={clsx(block, className)}>
      <h3 className={cn("main-title")}>{t("flightTrips.flightDetails")}</h3>

      <Accordion type="single" collapsible>
        {hasSeats && (
          <AccordionItem value="extras">
            <AccordionTrigger className={cn("accordion-trigger")}>
              <span className={cn("accordion-title")}>{t("trips.extras")}</span>
            </AccordionTrigger>
            <AccordionContent>
              <div className={cn("content")}>
                {/* Seats Section */}
                <div className={cn("section")}>
                  <h4 className={cn("section-title")}>
                    {t("flightTrips.seats")}
                  </h4>
                  {outboundSeats && (
                    <div className={cn("flight-direction")}>
                      <p className={cn("direction-label")}>
                        {t("flightTrips.outboundFlightsSeats")}
                      </p>
                      {outboundSeats.map((seat, idx) =>
                        renderSeatCard(seat, idx)
                      )}
                    </div>
                  )}
                  {returnSeats && (
                    <div className={cn("flight-direction")}>
                      <p className={cn("direction-label")}>
                        {t("flightTrips.returnFlightsSeats")}
                      </p>
                      {returnSeats.map((seat, idx) =>
                        renderSeatCard(seat, idx)
                      )}
                    </div>
                  )}
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {hasSeats && hasLayovers && <div className={cn("divider")} />}

        {hasLayovers && (
          <AccordionItem value="layover-details">
            <AccordionTrigger className={cn("accordion-trigger")}>
              <span className={cn("accordion-title")}>
                {t("flightTrips.layoverDetails")}
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <div className={cn("content")}>
                {layoverSlices.map((slice, idx) =>
                  renderLayoverSlice(slice, idx)
                )}
              </div>
            </AccordionContent>
          </AccordionItem>
        )}
      </Accordion>
    </div>
  );
};
